import React from 'react'
import { CSVLink } from 'react-csv'
import { useFeatureFlags, flags } from 'lib/feature-flags'
import DialogContent from '@material-ui/core/DialogContent'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import Link from '@material-ui/core/Link'
import RecordBookingPaymentDialog from 'components/record-booking-payment-dialog'
import AccountRouterLink from 'components/account-router-link'
import { formatPhoneNumber } from 'utils/phoneNumber'
import { useStyles } from './styles'
import { toFullDateAndTime } from 'utils/dateUtils'
import { currency } from 'lib/utils/string'
import { centsToDollars } from 'lib/utils/number'
import CancelEventRegistrationDialog from 'components/cancel-event-registration-dialog'
import Tooltip from '@material-ui/core/Tooltip'
import IconButton from '@material-ui/core/IconButton'
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined'
import PaymentIcon from '@material-ui/icons/Payment'
import StreamSendMessageButton, {
  MaintenanceSendMessageButton,
} from 'components/stream-messaging/send-message-button'
import MessageOutlined from '@material-ui/icons/MessageOutlined'
import { useRouteMatch, Link as LinkTo } from 'react-router-dom'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'

const columnHeaders = [
  'First Name',
  'Last Name',
  'Email',
  'Phone',
  'Canceled Registration',
  'Date Registered',
]
const hopeColumnHeaders = [
  'Accommodations',
  'Additional Info',
  'Golf Career Interest',
  'Golf Experience',
  'Handedness (L/R)',
  'Has Own Clubs',
  'Military Status',
  'Referral Source',
  'Service Branch',
  'Service Year Start',
  'Service Year End',
  'Shirt Size',
  'Emergency Contact First Name',
  'Emergency Contact Last Name',
  'Emergency Contact Phone',
  'Emergency Contact Email',
  'Emergency Contact Relationship',
]

const generateParticipantCsvData = (enrollments, isHopeEvent = false) => {
  const csvHeaders = isHopeEvent
    ? [...columnHeaders, ...hopeColumnHeaders]
    : columnHeaders

  return [
    csvHeaders,
    ...enrollments.map(e => {
      const { student } = e
      const email = student.representatives.find(r => r.contact.email != null)
        ?.contact.email
      const phone = student.representatives.find(r => r.contact.phone != null)
        ?.contact.phone
      const pgaHopeProfile = student.pgaHopeProfile
      const pgaHopeEmergencyContact = student.pgaHopeEmergencyContact
      const pgaHopeProfileData = [
        pgaHopeProfile?.accommodations || '',
        pgaHopeProfile?.additionalInfo || '',
        pgaHopeProfile?.golfCareerInterest || '',
        pgaHopeProfile?.golfExperience || '',
        pgaHopeProfile?.handedness || '',
        pgaHopeProfile?.hasOwnClubs || '',
        pgaHopeProfile?.militaryStatus || '',
        pgaHopeProfile?.referralSource || '',
        pgaHopeProfile?.serviceBranch || '',
        pgaHopeProfile?.serviceYearStart || '',
        pgaHopeProfile?.serviceYearEnd || '',
        pgaHopeProfile?.shirtSize || '',
        pgaHopeEmergencyContact?.firstName || '',
        pgaHopeEmergencyContact?.lastName || '',
        formatPhoneNumber(pgaHopeEmergencyContact?.phone),
        pgaHopeEmergencyContact?.email || '',
        pgaHopeEmergencyContact?.relationship || '',
      ]

      return [
        student.firstName,
        student.lastName,
        email || '',
        formatPhoneNumber(phone) || '',
        e.isCanceled || 'FALSE',
        e.createdAt,
        ...(isHopeEvent ? pgaHopeProfileData : []),
      ]
    }),
  ]
}

const HopeLinkWrapper = ({ condition, wrapper, children }) => {
  return condition ? wrapper(children) : children
}

const ParticipantsList = ({
  event,
  enrollments,
  onPayment,
  isHopeEvent = false,
}) => {
  const {
    title,
    startDateTime,
    priceInCents,
    acceptsOnlinePayments,
    slug,
  } = event
  const classes = useStyles()
  const { path } = useRouteMatch()

  const URL = `${path
    .split('/')
    .slice(0, 2)
    .join('/')}/${slug}`

  return (
    <Box className={classes.studentContent} width="100%">
      {enrollments.map(enrollment => {
        const { id: enrollmentId, student, createdAt, isCanceled } = enrollment
        const studentInitials = `${student.firstName.substring(
          0,
          1,
        )}${student.lastName.substring(0, 1)}`
        const studentName = `${student.firstName} ${student.lastName}`
        const isPaid = enrollment.transactions.some(t => !t.refund)
        const paymentAmount =
          (isPaid &&
            centsToDollars(enrollment.transactions[0].amountInCents)) ||
          0
        const paymentDate = isPaid && enrollment.transactions[0].createdAt
        const registrationPaymentId =
          isPaid && enrollment.transactions[0].registrationPayment.id
        const contactInformation = [
          ...new Set(
            student.representatives
              .flatMap(r => [
                r.contact.email,
                formatPhoneNumber(r.contact.phone),
              ])
              .filter(s => s != null && s.trim().length > 0),
          ),
        ].join(' | ')
        return (
          <Box
            key={enrollment.id}
            display="flex"
            alignItems="flex-start"
            mb={2}
          >
            <HopeLinkWrapper
              condition={isHopeEvent}
              wrapper={children => (
                <LinkTo
                  to={`${URL}/${student.id}`}
                  style={{
                    textDecoration: 'none',
                    color: 'rgba(0, 0, 0, 0.87)',
                  }}
                >
                  {children}
                </LinkTo>
              )}
            >
              <Avatar className={classes.avatarWrapper}>
                {studentInitials}
              </Avatar>
            </HopeLinkWrapper>
            <Box
              display="flex"
              flexDirection="row"
              flexWrap="wrap"
              justifyContent="space-between"
              flexGrow={1}
            >
              <Box>
                <HopeLinkWrapper
                  condition={isHopeEvent}
                  wrapper={children => (
                    <LinkTo
                      to={`${URL}/${student.id}`}
                      style={{
                        textDecoration: 'none',
                        color: 'rgba(0, 0, 0, 0.87)',
                      }}
                    >
                      {children}
                    </LinkTo>
                  )}
                >
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    {studentName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {contactInformation}
                  </Typography>
                </HopeLinkWrapper>
                <Typography variant="caption" display="block">
                  Registered {toFullDateAndTime(createdAt)}
                  &nbsp;|&nbsp;
                  {isPaid ? (
                    <Link
                      component={AccountRouterLink}
                      to={`/account/payments/transactions/${registrationPaymentId}`}
                      variant="caption"
                    >
                      Paid {currency(paymentAmount)} on{' '}
                      {toFullDateAndTime(paymentDate)}
                    </Link>
                  ) : (
                    <Typography variant="caption">Not Paid</Typography>
                  )}
                </Typography>
              </Box>
              <Box>
                {!isPaid && !isCanceled && (
                  <SensitiveInfoWrapper>
                    <RecordBookingPaymentDialog
                      trigger={
                        <Tooltip title="Mark as Paid">
                          <IconButton>
                            <PaymentIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                      }
                      enrollmentId={enrollmentId}
                      eventTitle={title}
                      eventDate={startDateTime}
                      participantName={studentName}
                      defaultAmountInCents={priceInCents}
                      acceptOnlinePayments={acceptsOnlinePayments}
                      onPaymentSuccess={onPayment}
                    />
                  </SensitiveInfoWrapper>
                )}
                {!isCanceled && (
                  <>
                    <CancelEventRegistrationDialog
                      trigger={
                        <Tooltip title="Cancel Event Registration">
                          <IconButton>
                            <DeleteOutlinedIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                      }
                      studentId={student.id}
                      eventId={event.id}
                    />
                  </>
                )}
              </Box>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

const EventParticipants = ({ setIsOpen, event, onPayment }) => {
  const { tags } = event
  const classes = useStyles()
  const canceledEnrollments = event.enrollments.filter(e => e.isCanceled)
  const enrollments = event.enrollments.filter(e => !e.isCanceled)
  const representatives = enrollments.flatMap(e => e.student.representatives)
  const contactExternalIds = representatives.map(rep => rep.contact.externalId)
  const isHopeEvent = tags.includes('PGA HOPE')

  const [enabledMessagingService] = useFeatureFlags([
    flags.FLAG_MESSAGING_SERVICE,
  ])

  const sendMessageButtonProps = {
    component: IconButton,
    color: 'primary',
    children: <MessageOutlined />,
  }

  const SendMessageButton = (() => {
    switch (enabledMessagingService) {
      case 'stream':
        return () => (
          <SensitiveInfoWrapper>
            <StreamSendMessageButton
              userIds={contactExternalIds}
              {...sendMessageButtonProps}
            />
          </SensitiveInfoWrapper>
        )
      case 'maintenance':
      default:
        return () => (
          <MaintenanceSendMessageButton {...sendMessageButtonProps} />
        )
    }
  })()

  return (
    <DialogContent>
      <Box
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        className={classes.backButtonWrapper}
      >
        <Box
          display="flex"
          alignItems="center"
          onClick={() => setIsOpen('detail')}
        >
          <ArrowBackIosIcon fontSize="small" />
          <Typography variant="caption" style={{ fontWeight: 900 }}>
            Back
          </Typography>
        </Box>
        <Box>
          <SendMessageButton />
          <Button
            component={CSVLink}
            data={generateParticipantCsvData(event.enrollments, isHopeEvent)}
            filename={`${event.title}-${event.startDateTime}-participants.csv`}
            variant="contained"
            color="primary"
          >
            EXPORT
          </Button>
        </Box>
      </Box>
      <Box display="flex" alignItems="flex-start" flexDirection="column">
        <Typography variant="h6" className={classes.titleFont}>
          {event.title}
        </Typography>
        <Typography variant="h4" className={classes.headerFont}>
          Participants ({enrollments.length})
        </Typography>
        <ParticipantsList
          event={event}
          enrollments={enrollments}
          onPayment={onPayment}
          isHopeEvent={isHopeEvent}
        />
        {canceledEnrollments.length > 0 && (
          <>
            <hr />
            <Typography variant="h6" className={classes.headerFont}>
              Canceled Participants ({canceledEnrollments.length})
            </Typography>
            <ParticipantsList
              event={event}
              enrollments={canceledEnrollments}
              isHopeEvent={isHopeEvent}
            />
          </>
        )}
      </Box>
    </DialogContent>
  )
}

export default EventParticipants
